import { generateId, getLabelFromValue } from "../../../../utils/arrayUtils";
import { sortQuotationRows } from "../../../../utils/constants/DemandConstant";

/* -------------------- POP HELPERS -------------------- */
export function makePopName(index, detail,regionOptions) {
  const base = `Access PoP`;
  const regionLabel = getLabelFromValue(regionOptions, detail.region) || "";
  return regionLabel ? `${base} ${detail.region?`- ${regionLabel}`:""}` : base;
}

export function buildPopsRows(popsDetails, quotationRows,regionOptions) {
  const isPopRow = (r) => r?.group === "pops";
  const existingPopRows = quotationRows?.filter(isPopRow) || [];
  const nonPop = quotationRows?.filter((r) => !isPopRow(r)) || [];
  const nextPops = popsDetails?.map((detail, i) => {
    const oldRow = existingPopRows.find((r) => r.popId === detail.id);
    const popInactive = !detail?.region || !detail?.portSize;
    return {
      ipxService: makePopName(i, detail,regionOptions),
      setupCost: popInactive ? "0" : oldRow?.setupCost ?? "0",
      monthlyCharges: popInactive ? "0" : oldRow?.monthlyCharges ?? "0",
      usageBased: popInactive ? "0" : oldRow?.usageBased ?? "0",
      group: "pops",
      popId: detail.id,
    };
  });
  return sortQuotationRows([...nextPops, ...nonPop]);
}

export function applyPopRegionChange(values, index, newRegion) {
  const pops = [...values?.serviceConfig?.ipAccessStandardConnection?.popsDetails];
  pops[index].region = newRegion || [];

  const newRows = buildPopsRows(pops, values?.quotation?.rows);

  return {
    ...values,
    serviceConfig: {
      ...values?.serviceConfig,
      ipAccessStandardConnection: {
        ...values?.serviceConfig?.ipAccessStandardConnection,
        popsDetails: pops
      }
    },
    quotation: { ...values.quotation, rows: newRows }
  };
}

/* -------------------- GRX HELPERS -------------------- */
export function buildGrxBandwidthRows(bandwidths, quotationRows) {
  const isGrx = (r) => r?.group === "grx-bandwidth";
  const existingGrxRows = quotationRows.filter(isGrx);
  const nonGrx = quotationRows?.filter((r) => !isGrx(r));
  const next = bandwidths?.map((bw) => {
    const cap = bw.allocatedBandwidth || "";
    const oldRow = existingGrxRows.find( r => r?.bandwidthId === bw?.id);
    const bandwidthInactive = !bw?.allocatedBandwidth;
    return {
      ipxService: `GRX/Transport up to ${cap} MBPS`,
      setupCost: bandwidthInactive? "0" : oldRow?.setupCost ?? "0",
      monthlyCharges: bandwidthInactive? "0" : oldRow?.monthlyCharges ?? "0",
      usageBased: bandwidthInactive? "0" : oldRow?.usageBased ?? "0",
      group: "grx-bandwidth",
      bandwidthId: bw.id
    };
  });

  return sortQuotationRows([...next, ...nonGrx]);
}

export function applyTransportChange(values, fieldValue) {
  const transportOff = fieldValue !== "yes";
  let bandwidths = [...values?.serviceConfig?.IpxGrxTransport?.bandwidths];
  if (transportOff) {
    bandwidths = [{ id: bandwidths[0]?.id ?? generateId(),allocatedBandwidth: "" }];
  }
  let newRows = buildGrxBandwidthRows(bandwidths,values?.quotation.rows);
  if (transportOff) {
    newRows = newRows.map(row =>
      row.group === "grx-bandwidth"
        ? {...row,setupCost: "0",monthlyCharges: "0",usageBased: "0"}: row
    );
  }
  return {
    ...values,
    serviceConfig: {
      ...values.serviceConfig,
      IpxGrxTransport: {
        ...values.serviceConfig.IpxGrxTransport,
        isIpxGrxTransport: fieldValue,
        bandwidths
      }
    },
    quotation: {...values.quotation,rows: newRows}
  };
}

/* -------------------- SIGTRAN -------------------- */
export function buildSigtranMsusRow(msus, quotationRows) {
  const isSigtran = (r) => r?.group === "sigtran";
  const existingSigtranRows = quotationRows.filter(isSigtran);
  const nonDiameter = quotationRows?.filter((r) => !isSigtran(r));
  const next = msus?.map((msu) => {
    const cap = msu.allocatedMsus || "";
    const oldRow = existingSigtranRows.find( r => r?.msuId === msu?.id);
    const msuInactive = !msu?.allocatedMsus;
    return {
      ipxService: `IPX Sigtran ${cap ? "("+cap+" MSUs)":""} `,
      setupCost: msuInactive ? "0" : oldRow?.setupCost ?? "0",
      monthlyCharges: msuInactive ? "0" : oldRow?.monthlyCharges ?? "0",
      usageBased: msuInactive ? "0" : oldRow?.usageBased ?? "0",
      group: "sigtran",
      msuId: msu.id
    };
  });

  return sortQuotationRows([...next, ...nonDiameter]);
}
export function applySigtranChange(values, fieldValue) {
  const sigtranOff = fieldValue !== "yes";
  let msus = [...values.serviceConfig.IpxSigtran.msus];
  if (sigtranOff) {
    msus = [{id: msus[0]?.id ?? generateId(), allocatedMsus: "" }];
  }
  let rows = buildSigtranMsusRow(msus, values.quotation.rows);
  if (sigtranOff) {
    rows = rows.map(row =>
      row.group === "sigtran"
        ? { ...row, setupCost: "0", monthlyCharges: "0", usageBased: "0" }
        : row
    );
  }
  return {
    ...values,
    serviceConfig: {
      ...values.serviceConfig,
      IpxSigtran: {
        ...values.serviceConfig.IpxSigtran,
        isIpxSigtran: fieldValue,
        msus
      }
    },
    quotation: { ...values.quotation, rows }
  };
}
/* -------------------- DIAMETER -------------------- */
export function buildDiameterTransactionRow(numberOfTransactions, quotationRows) {
  const isDiameter = (r) => r?.group === "diameter";
  const existingDiameterRows = quotationRows.filter(isDiameter);
  const nonDiameter = quotationRows?.filter((r) => !isDiameter(r));
  const next = numberOfTransactions?.map((transaction) => {
    const cap = transaction.transaction || "";
    const oldRow = existingDiameterRows.find( r => r?.transactionId === transaction?.id);
    const transactionInactive = !transaction?.transaction;
    return {
      ipxService: `IPX Diameter ${cap? "("+cap+" Transactions)":""} `,
      setupCost: transactionInactive ? "0" : oldRow?.setupCost ?? "0",
      monthlyCharges: transactionInactive ? "0" : oldRow?.monthlyCharges ?? "0",
      usageBased: transactionInactive ? "0" : oldRow?.usageBased ?? "0",
      group: "diameter",
      transactionId: transaction.id
    };
  });

  return sortQuotationRows([...next, ...nonDiameter]);
}
export function applyDiameterChange(values, fieldValue) {
  const diameterOff = fieldValue !== "yes";
  let numberOfTransactions = [...values?.serviceConfig?.IpxDiameter?.numberOfTransactions];
  if (diameterOff) {
    numberOfTransactions = [{id: numberOfTransactions[0]?.id ?? generateId(), transaction: "" }];
  }
  let newRows = buildDiameterTransactionRow(numberOfTransactions,values?.quotation.rows);
  if (diameterOff) {
    newRows = newRows.map(row =>
      row.group === "diameter"
        ? {...row,setupCost: "0",monthlyCharges: "0",usageBased: "0"}: row
    );
  }
  return {
    ...values,
    serviceConfig: {
      ...values.serviceConfig,
      IpxDiameter: {
        ...values.serviceConfig.IpxDiameter,
        isIpxDiameter: fieldValue,
        numberOfTransactions
      }
    },
    quotation: {...values.quotation,rows: newRows}
  };
}
/* -------------------- HTTP -------------------- */
export function buildHttpRow(transactionVolumes, quotationRows) {
  const isHttp = (r) => r?.group === "http";
  const existingHttpRows = quotationRows.filter(isHttp);
  const nonHttp = quotationRows?.filter((r) => !isHttp(r));
  const next = transactionVolumes?.map((allocatedTransactionVolumes) => {
    const cap = allocatedTransactionVolumes.allocatedTransactionVolumes || "";
    const oldRow = existingHttpRows.find( r => r?.transactionVolumeId === allocatedTransactionVolumes?.id);
    const allocatedTransactionVolumesInactive = !allocatedTransactionVolumes?.allocatedTransactionVolumes;
    return {
      ipxService: `IPX HTTP2 ${cap? "("+cap+" Transaction Volumes)":""} `,
      setupCost: allocatedTransactionVolumesInactive ? "0" : oldRow?.setupCost ?? "0",
      monthlyCharges: allocatedTransactionVolumesInactive ? "0" : oldRow?.monthlyCharges ?? "0",
      usageBased: allocatedTransactionVolumesInactive ? "0" : oldRow?.usageBased ?? "0",
      group: "http",
      transactionVolumeId: allocatedTransactionVolumes.id
    };
  });

  return sortQuotationRows([...next, ...nonHttp]);
}
export function applyHttpChange(values, fieldValue) {
  const httpOff = fieldValue !== "yes";
  let transactionVolumes = [...values?.serviceConfig?.IpxHttp?.transactionVolumes];
  if (httpOff) {
    transactionVolumes = [{id: transactionVolumes[0]?.id ?? generateId(), allocatedTransactionVolumes: "" }];
  }
  let newRows = buildHttpRow(transactionVolumes,values?.quotation.rows);
  if (httpOff) {
    newRows = newRows.map(row =>
      row.group === "http"
        ? {...row,setupCost: "0",monthlyCharges: "0",usageBased: "0"}: row
    );
  }
  return {
    ...values,
    serviceConfig: {
      ...values.serviceConfig,
      IpxHttp: {
        ...values.serviceConfig.IpxHttp,
        isIpxHttp: fieldValue,
        transactionVolumes
      }
    },
    quotation: {...values.quotation,rows: newRows}
  };
}
/* -------------------- DNS -------------------- */
export function applyDnsChange(values, newValue) {
  const rows = values.quotation.rows.map((row) => {
    if (row.group === "dns" && newValue !== "yes") {
      return { ...row, setupCost: "0", monthlyCharges: "0", usageBased: "0" };
    }
    return row;
  });

  return {
    ...values,
    serviceConfig: {
      ...values.serviceConfig,
      IsIpxDns: newValue
    },
    quotation: { ...values.quotation, rows }
  };
}